<template>
  <div>
    <div
      :class="[
        'border border-secondary px-4 py-6 rounded items-center text-center flex flex-col',
        { 'bg-white': props.pageName === 'manual-payment' },
      ]"
      v-if="props.mobile"
    >
      <div class="mb-2">
        <NuxtImg loading="lazy" src="/icons/b-meeting.svg" alt="" class="w-14" />
      </div>
      <div class="m-2 flex flex-col space-y-4">
        <h3 class="">{{ $t("chat.title") }}</h3>
        <div class="leading-7">{{ $t("chat.desc") }}</div>
      </div>
      <div v-if="phoneHref" class="w-full mt-4 space-y-2">
        <p class="text-sm font-bold uppercase tracking-wider">{{ $t("chat.speakToAdvisor") }}</p>
        <a :href="phoneHref" :class="[ctaClass, 'w-full px-6 py-3 text-[20px] leading-7', props.btnClass]">
          <NuxtImg src="/icons/phone.svg" alt="" aria-hidden="true" class="w-6 h-6 brightness-0 invert" />
          <span>{{ displayNumber }}</span>
        </a>
      </div>
    </div>

    <div
      v-else
      :class="{
        'border border-secondary px-4 md:px-8 lg:px-16 py-6 md:mx-8 my-14 md:my-20 grid md:grid-flow-col md:grid-rows-1 rounded md:items-center md:text-left text-center':
          position != 'menu',
        'flex flex-col text-center': position == 'menu',
        'bg-white': props.pageName === 'manual-payment',
      }"
    >
      <div
        class="place-self-center"
        :class="{
          'space-y-4 md:pr-6 md:place-self-start': position != 'menu',
          'order-1 my-8': position == 'menu',
        }"
      >
        <NuxtImg
          loading="lazy"
          :class="{
            'h-10 w-10': position == 'menu',
          }"
          src="/icons/b-meeting.svg"
          alt="meeting icon"
          width="62"
          height="60"
        />
      </div>
      <div
        class="space-y-2 md:my-0 my-4"
        :class="{
          'order-0': position == 'menu',
        }"
      >
        <h3
          :class="{
            'text-sm leading-6': position == 'menu',
          }"
        >
          {{ $t("chat.title") }}
        </h3>
        <div
          class="text-blog-14-sm"
          :class="{
            'text-xs': position == 'menu',
          }"
        >
          {{ $t("chat.desc") }}
        </div>
      </div>
      <div
        v-if="phoneHref"
        class="place-self-center lg:place-self-end w-full md:w-auto text-center space-y-2"
        :class="{
          'md:pl-6 mt-4 md:mt-0': position != 'menu',
          'order-2': position == 'menu',
        }"
      >
        <p
          class="font-bold uppercase tracking-wider"
          :class="{ 'text-sm': position != 'menu', 'text-xs': position == 'menu' }"
        >
          {{ $t("chat.speakToAdvisor") }}
        </p>
        <a
          :href="phoneHref"
          :class="[
            ctaClass,
            position == 'menu' ? 'w-full px-4 py-3 text-lg' : 'w-full md:w-auto px-6 py-3 text-[20px] leading-7',
            props.btnClass,
          ]"
        >
          <NuxtImg
            src="/icons/phone.svg"
            alt=""
            aria-hidden="true"
            class="brightness-0 invert"
            :class="position == 'menu' ? 'w-5 h-5' : 'w-6 h-6'"
          />
          <span>{{ displayNumber }}</span>
        </a>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useGlobalStore } from "~/stores/global";

export interface LiveChatBlockProps {
  mobile?: boolean;
  btnClass?: string;
  position?: string;
  imageClass?: string;
  pageName?: string;
}

const props = withDefaults(defineProps<LiveChatBlockProps>(), {
  btnClass: "",
  mobile: false,
  position: "",
  imageClass: "",
  pageName: "",
});

const globalStore = useGlobalStore();

// Shared look for the phone call-to-action button
const ctaClass =
  "inline-flex items-center justify-center gap-3 rounded-lg bg-secondary text-white font-bold whitespace-nowrap hover:bg-secondary-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary transition-colors";

// Phone data comes from the CMS; render no CTA rather than a broken "tel:undefined" link if it is missing
const phoneHref = computed(() => {
  const phone = globalStore.sitePrimaryPhoneNumber;
  if (!phone?.call_to_action_number) return null;
  return `tel:${phone.country_code ?? ""}${phone.call_to_action_number}`;
});

const displayNumber = computed(() => globalStore.sitePrimaryPhoneNumber?.display_number ?? "");
</script>
